// @flow

import * as React from 'react';

import hoistNonReactStatics from 'hoist-non-react-statics';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
  UNSAFE_NavigationContext,
} from 'react-router-dom';

import type { Location as LocationBase } from 'react-router-dom';
import { superpathjoin } from 'superpathjoin';

import { mapURLSearchParamsToQuery } from '@gandi/app-runner';

export type QueryValue = string | $ReadOnlyArray<string>;

/**
 * Replacement for `location.query` provided by withRouter which is deprecated
 */
export const useQuery = <Q>(): Partial<Q> => {
  const [searchParams] = useSearchParams();
  return mapURLSearchParamsToQuery(searchParams);
};

export type Params = { +[key: string]: string, ... };
export type Query = Record<string, string>;
export type State = { +[key: string]: string, ... };

/**
 * Careful with this type, it only match the Location given by withRouter
 * @deprecated: this type should be replaced by original Location cf. injected `query` prop
 */
export type CustomLocation<Q = Query, S = State> = {
  ...$Exact<LocationBase>,
  // injected cf. `withRouter`
  /**
   * @deprecated: use `useSearchParams` instead or searchParams cf. `WithRouterProps`
   */
  query: Q,
  // overriden for more precise types
  state?: S,
};

type NavigateFunction = ReturnType<typeof useNavigate>;
type UseSearchParamsReturn = ReturnType<typeof useSearchParams>;

export type WithRouterProps<P = Params, Q = Query, S = State> = {
  /**
   * @deprecated make sure you don't use `location.query`
   */
  location: CustomLocation<Q, S>,
  navigate: NavigateFunction,
  params: P,
  searchParams: UseSearchParamsReturn[0],
  setSearchParams: UseSearchParamsReturn[1],
  /**
   * @deprecated prefer using `useQuery` instead
   */
  query: Q,
};

export function withRouter<Props>(
  Component: React.ComponentType<Props>,
  // $FlowFixMe[unclear-type]
): React.ComponentType<any> {
  const Wrapper = (props: Props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const query = useQuery<any>();
    // TODO: remove, this leads to confusing custom and regular Location types
    // ⚠️ this doesn't use the same implementation as before nor `useQuery`
    // $FlowFixMe[prop-missing]
    location.query = Object.fromEntries(searchParams);

    return (
      <Component
        {...props}
        location={location}
        navigate={useNavigate()}
        params={useParams()}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        query={query}
      />
    );
  };

  return hoistNonReactStatics(Wrapper, Component);
}

/**
 * Temporary helper to get an absolute url for page reloading.
 * This dirty reimplement useHref and expose a function.
 * It overrides current location with the `to` prop
 *
 * @deprecated: implement `useHref` or get rid of global page reload
 */
export function useCreateHref(): (to: Partial<LocationBase>) => string {
  const { basename, navigator } = React.useContext(UNSAFE_NavigationContext);
  const { pathname: currentPathname, ...location } = useLocation();

  return to => {
    // $FlowFixMe[cannot-spread-inexact]
    const nextTo = {
      ...location,
      ...to,
      pathname: superpathjoin(basename, to.pathname || currentPathname),
    };

    return navigator.createHref(nextTo);
  };
}
